import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import * as storiesService from '@/services/supabase/stories';
import type { Story } from '@/services/supabase/stories';

// Create a singleton QueryClient that can be used outside of React components
// WARN: This creates a separate cache from the React app. Only use for non-React contexts or testing.
const queryClient = new QueryClient();

/**
 * Get stories for a project from the TanStack Query cache or fetch them if not cached
 * @param projectId - Project ID
 * @returns Promise with array of stories
 */
export const getStoriesFromCache = async (projectId: string): Promise<Story[]> => {
  // Try to get stories from the cache first
  const cachedStories = queryClient.getQueryData<Story[]>(
    queryKeys.stories.byProject(projectId)
  );

  // If stories are in the cache, return them
  if (cachedStories) {
    return cachedStories;
  }

  // If not in cache, fetch them directly and update the cache
  try {
    const stories = await storiesService.getStoriesByProject(projectId);

    // Update the cache with the fetched stories
    queryClient.setQueryData(
      queryKeys.stories.byProject(projectId),
      stories
    );

    return stories;
  } catch (error) {
    console.error('Error fetching stories:', error);
    throw error;
  }
};

/**
 * Get a story by ID from the TanStack Query cache or fetch it if not cached
 * @param storyId - Story ID
 * @param includeFull - Whether to include full related data
 * @returns Promise with story
 */
export const getStoryFromCache = async <T extends boolean = false>(
  storyId: string,
  includeFull: T = false as T
): Promise<any> => {
  // Determine the query key based on whether to include full data
  const queryKey = includeFull
    ? queryKeys.stories.full(storyId)
    : queryKeys.stories.detail(storyId);

  // Try to get the story from the cache first
  const cachedStory = queryClient.getQueryData(queryKey);

  // If the story is in the cache, return it
  if (cachedStory) {
    return cachedStory;
  }

  // If not in cache, fetch it directly and update the cache
  try {
    const story = await storiesService.getStoryById(storyId, includeFull);

    // Update the cache with the fetched story
    queryClient.setQueryData(queryKey, story);

    return story;
  } catch (error) {
    console.error('Error fetching story:', error);
    throw error;
  }
};
